import { ShippingInfoInteface } from '../pages/case/shipping-info/shipping-info.component'
import { Attachment } from './attachment.model'
import { Customer } from './customer.model'
import { DesignParameter } from './design-parameter.model'
import { DesignTaskSummary } from './design-task.model'
import { Invoice, InvoiceStatus } from './invoice.model'
import { TicketProduct } from './product.model'
import { User } from './user.model'

export const ticketTypeOption = [
  {
    label: 'Product',
    value: 'product',
  },
  {
    label: 'Rush',
    value: 'rush',
  },
]

export const inMasterOptions = [
  {
    label: 'Included',
    value: 'true',
  },
  {
    label: 'Excluded',
    value: 'false',
  },
]

export class Ticket {
  id: number = 0
  createdAt: Date = new Date()
  updatedAt: Date = new Date()
  note?: string
  status?: string
  designer?: User
  qualityController?: User
  benchQc?: User
  benchTech?: User
  author?: Customer
  owner?: Customer
  designCollaborators: User[] = []
  qcCollaborators: User[] = []
  attachments: Attachment[] = []
  transitions: Transition[] = []
  totalPrice: number = 0
  totalDuration: number = 0
  dueDate: Date = new Date()
  rush: boolean = false
  invoice?: Invoice
  products: TicketProduct[] = []
  completedDate: Date | null = null
  ticketNumber: string = ''
  designParameters: DesignParameter | null = null
  shipmentProvider: ShippingInfoInteface | null = null
  subtotal: number = 0;
  discount: number = 0;
  designTaskSummary?: DesignTaskSummary;

  constructor(data?: any) {
    if (data) {
      this.id = data?.id
      this.createdAt = data?.createdAt
      this.updatedAt = data?.updatedAt
      this.note = data?.note
      this.status = data?.status
      this.designer = new User(data?.designer)
      this.qualityController = new User(data?.qualityController)
      this.benchQc = new User(data?.benchQc)
      this.benchTech = new User(data?.benchTech)
      this.author = new Customer(data?.author)
      this.owner = new Customer(data?.owner)
      this.totalPrice = data?.totalPrice
      this.totalDuration = data?.totalDuration
      this.dueDate = data?.dueDate
      this.rush = data?.rush
      this.completedDate = data?.completedDate
      this.ticketNumber = data?.ticketNumber
      this.designParameters = data?.designParameters
      this.shipmentProvider = data?.shipmentProvider
      this.discount = data?.discount
      this.subtotal = data?.subtotal

      if (data.invoice) {
        this.invoice = new Invoice(data?.invoice)
      }

      if (data?.designCollaborators) {
        this.designCollaborators = data.designCollaborators.map((d: any) => new User(d))
      }

      if (data?.qcCollaborators) {
        this.qcCollaborators = data.qcCollaborators.map((d: any) => new User(d))
      }

      if (data?.attachments) {
        this.attachments = data.attachments.map((d: any) => new Attachment(d))
      }

      this.transitions = []
      if (data?.transitions) {
        this.transitions = data.transitions.map((d: any) => new Transition(d))
      }

      if (data?.products) {
        this.products = data.products.map((p: any) => new TicketProduct(p))
      }

      if (data?.designTaskSummary) {
        this.designTaskSummary = data.designTaskSummary
      }
    }
  }

  isActive(): boolean {
    switch (this.status) {
      case 'Waiting For Validation':
      case 'Ready':
      case 'In Progress':
      case 'On Hold':
      case 'Waiting For QC':
        return true
    }

    return false
  }

  isCalculatingTime(): boolean {
    switch (this.status) {
      case 'Ready':
      case 'In Progress':
      case 'On Hold':
      case 'Waiting For QC':
        return true
    }

    return false
  }

  allowUpdateproducts(): boolean {
    if (this.isPaid()) {
      return false
    }
    
    switch (this.status) {
      case 'New':
      case 'Payment Pending':
      case 'Waiting For Validation':
      case 'Ready':
      case 'In Progress':
      case 'On Hold':
      case 'Waiting For QC':
        return true
    }

    return false
  }

  isPaid(): boolean {
    if (!this.invoice) {
      return false
    }

    return this.invoice && this.invoice.status == "Completed";
  }

  getDuration = () => {
    const days = Math.trunc(this.totalDuration / 24 / 3600)
    const hours = Math.trunc(this.totalDuration / 3600) - 24 * days
    const minutes = Math.trunc((this.totalDuration % 3600) / 60)

    let result = ''

    if (days > 0) {
      result = `${days} days`
    }

    if (hours > 0) {
      result = `${hours} hours`
    }

    if (minutes > 0) {
      result += ` ${minutes} minutes`
    }

    return result.trim()
  }

  canEditDiscount(): boolean {
    if (this.invoice?.status != InvoiceStatus.Completed) {
      return true
    }

    return false
  }

  isRunning(): boolean {
    switch (this.status) {
      case 'Ready':
      case 'Payment Pending':
      case 'Waiting For Validation':
        return true
    }

    return false
  }

  shouldHighlight(): boolean {
    if (this.isRunning() && !this.designTaskSummary?.designers.length) {
      return true
    }

    return false
  }
}

export class Transition {
  name: string
  to: string
  display?: string

  constructor(data?: any) {
    this.name = data?.name
    this.to = data?.to
  }
}

export class TicketPatch {
  note?: string
  designerId?: number
  qualityControllerId?: number
  benchQcId?: number
  benchTechId?: number
  designCollaborators?: number[]
  qcCollaborators?: number[]
  shipmentProvider?: ShippingInfoInteface
}

export class TransitionTicketPayload extends Transition {
  note: string = ''
}

export const FieldMapping: any = {
  id: 'ID',
  createdAt: 'Created At',
  note: 'Note',
  status: 'Status',
  designer: 'Designer',
  qualityController: 'Quality Controller',
  benchQc: 'Bench QC',
  benchTech: 'Bench Tech',
  designCollaborators: 'Design Collaborators',
  qcCollaborators: 'QC Collaborators',
  attachments: 'Attachments',
  transitions: 'Transitions',
  products: 'Products',
  outputs: 'Outputs',
}

export const StateColorMapping: any = {
  New: 'magenta',
  'Payment Pending': 'red',
  'Waiting For Validation': 'volcano',
  Ready: 'orange',
  'In Progress': 'blue',
  'On Hold': 'cyan',
  'Waiting For QC': 'geekblue',
  Completed: 'purple',
  Closed: 'lime',
}

export class TicketProductPayload {
  productID: number
  quantity: number
  price: number

  constructor(productId: number, quantity: number, price: number) {
    this.productID = productId
    this.quantity = quantity
    this.price = price
  }
}
